import { useState, useEffect } from "react";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { useDomains, useDnsHealth } from "@/api/hooks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { DnsCheck } from "@/api/client";

export default function DnsPage() {
  const { data: domainsData } = useDomains();
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    if (!active && domainsData?.domains.length) {
      setActive(domainsData.domains[0].name);
    }
  }, [domainsData, active]);

  const health = useDnsHealth(active);

  const summary = health.data
    ? summarize(health.data.checks)
    : { pass: 0, warn: 0, fail: 0 };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="text-heading text-3xl font-light sm:text-4xl">
            <span className="text-bitrefinery-red">D</span>NS health
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-bitrefinery-text text-body">
            Live checks of MX, SPF, DMARC, DKIM (for every local selector), and the PTR on
            the MX target. Run this before flipping a domain's MX to production.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => health.refetch()}
          disabled={health.isFetching}
        >
          <ArrowPathIcon className="h-4 w-4" />
          Recheck
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {domainsData?.domains.map((d) => (
          <button
            key={d.name}
            onClick={() => setActive(d.name)}
            className={
              active === d.name
                ? "rounded-full border border-bitrefinery-red bg-bitrefinery-red px-3 py-1 text-sm text-white"
                : "rounded-full border bg-card px-3 py-1 text-sm hover:bg-accent"
            }
          >
            {d.name}
          </button>
        ))}
      </div>

      {health.isLoading && <p className="text-muted-foreground">Resolving…</p>}

      {health.data && (
        <div className="space-y-4">
          <div className="flex gap-3 text-sm">
            <Badge variant="secondary">
              <CheckCircleIcon className="mr-1 inline h-3 w-3" />
              {summary.pass} pass
            </Badge>
            <Badge variant="outline">
              <ExclamationTriangleIcon className="mr-1 inline h-3 w-3" />
              {summary.warn} warn
            </Badge>
            <Badge variant="destructive">
              <ExclamationCircleIcon className="mr-1 inline h-3 w-3" />
              {summary.fail} fail
            </Badge>
            <span className="text-xs text-muted-foreground">
              checked {new Date(health.data.checkedAt).toLocaleTimeString()}
            </span>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <ul className="divide-y">
              {health.data.checks.map((c) => (
                <CheckRow key={c.key} c={c} />
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function CheckRow({ c }: { c: DnsCheck }) {
  const tone =
    c.status === "pass"
      ? "text-emerald-600"
      : c.status === "warn"
        ? "text-amber-600"
        : c.status === "fail"
          ? "text-rose-600"
          : "text-muted-foreground";
  const Icon =
    c.status === "pass"
      ? CheckCircleIcon
      : c.status === "warn"
        ? ExclamationTriangleIcon
        : c.status === "fail"
          ? ExclamationCircleIcon
          : InformationCircleIcon;
  return (
    <li className="flex gap-4 px-4 py-3">
      <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${tone}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{c.label}</span>
          <Badge
            variant={
              c.status === "pass"
                ? "secondary"
                : c.status === "warn"
                  ? "outline"
                  : c.status === "fail"
                    ? "destructive"
                    : "outline"
            }
            className="text-[10px]"
          >
            {c.status}
          </Badge>
        </div>
        {c.expected && (
          <div className="mt-0.5 text-xs text-muted-foreground">
            expected: <span className="font-mono">{c.expected}</span>
          </div>
        )}
        {c.actual && (
          <div className="mt-0.5 break-all text-xs">
            actual: <span className="font-mono">{c.actual}</span>
          </div>
        )}
        {c.note && <p className="mt-1 text-xs text-muted-foreground">{c.note}</p>}
      </div>
    </li>
  );
}

function summarize(checks: DnsCheck[]) {
  const out = { pass: 0, warn: 0, fail: 0 };
  for (const c of checks) {
    if (c.status === "pass") out.pass++;
    else if (c.status === "warn") out.warn++;
    else if (c.status === "fail") out.fail++;
  }
  return out;
}
